import { Head, Link, router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, RotateCcw, Target } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { AskAssistantButton } from "@/components/assistant/AskAssistantButton"
import { HelpfulPrompt } from "@/components/quizzes/HelpfulPrompt"
import { AreaBreakdown } from "@/components/quizzes/AreaBreakdown"
import { CHOICE_LETTERS } from "@/components/quizzes/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import type { AreaResult, AttemptSummary, MissedQuestion } from "@/types/quizzes"

export default function QuizResults({
  attempt,
  quiz,
  areas,
  missed,
}: {
  attempt: AttemptSummary & { correct_count: number }
  quiz: { id: number; title: string }
  areas: AreaResult[]
  missed: MissedQuestion[]
}) {
  const intl = useIntl()
  const format = useFormat()
  const title = intl.formatMessage({ id: "quizzes.results_title" }, { title: quiz.title })
  const description = intl.formatMessage({ id: "quizzes.results_meta" }, { title: quiz.title })
  const startAttempt = (data: Record<string, number> = {}) => router.post(`/quizzes/${quiz.id}/attempts`, data)

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link href={`/quizzes/${quiz.id}`} className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {quiz.title}
        </Link>
        <PageHeader
          title={intl.formatMessage({ id: "quizzes.results" })}
          description={
            <>
              {attempt.retry && (
                <Badge tone="muted" className="mr-2">
                  {intl.formatMessage({ id: "quizzes.retry_badge" })}
                </Badge>
              )}
              {quiz.title}{attempt.finished_on && ` · ${format.date(attempt.finished_on, { month: "short", day: "numeric", year: "numeric" })}`}
            </>
          }
        />

        <div className="callout mt-8 text-center">
          <h2 className="text-5xl font-semibold">{format.number((attempt.score ?? 0) / 100, { style: "percent" })}</h2>
          <p className="mt-1">
            {intl.formatMessage({ id: "quizzes.score_line" }, { correct: attempt.correct_count, questions: format.questionCount(attempt.question_count) })}
            {attempt.retry && ` ${intl.formatMessage({ id: "quizzes.retry_note" })}`}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Button type="button" variant="secondary" onClick={() => startAttempt()}>
              <RotateCcw className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.retake" })}
            </Button>
            {missed.length > 0 && (
              <Button type="button" onClick={() => startAttempt({ retry_of: attempt.id })}>
                <Target className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.retry_missed" }, { count: missed.length })}
              </Button>
            )}
          </div>
        </div>

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "quizzes.by_area" })}</h2>
          <AreaBreakdown areas={areas} />
        </section>

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "quizzes.missed_title" })}</h2>
          {missed.length === 0 ? (
            <p className="mt-2">{intl.formatMessage({ id: "quizzes.none_missed" })}</p>
          ) : (
            <ol className="mt-4 space-y-4 p-0">
              {missed.map((question) => (
                <li key={question.id} className="list-none rounded-xl border border-hairline bg-page p-4">
                  {question.content_area_name && (
                    <Badge tone="muted" className="mb-2">
                      {question.content_area_name}
                    </Badge>
                  )}
                  <p className="whitespace-pre-wrap break-words">
                    <strong>{question.text}</strong>
                  </p>
                  <dl className="mt-3 space-y-1 text-sm">
                    <div className="flex gap-2">
                      <dt className="shrink-0 text-ink-muted">{intl.formatMessage({ id: "quizzes.your_answer" })}</dt>
                      <dd className="min-w-0 break-words text-danger-display">
                        {CHOICE_LETTERS[question.chosen_choice]}. {question.choices[question.chosen_choice]}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="shrink-0 text-ink-muted">{intl.formatMessage({ id: "quizzes.correct_answer" })}</dt>
                      <dd className="min-w-0 break-words text-accent-display">
                        {CHOICE_LETTERS[question.correct_choice]}. {question.choices[question.correct_choice]}
                      </dd>
                    </div>
                  </dl>
                  {question.explanation && <p className="mt-3 whitespace-pre-wrap">{question.explanation}</p>}
                  <div className="mt-3">
                    <HelpfulPrompt key={question.id} attemptId={attempt.id} questionId={question.id} />
                  </div>
                  <div className="mt-3">
                    <AskAssistantButton attemptId={attempt.id} questionId={question.id} />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </AppShell>
    </>
  )
}
